import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Shield, BarChart3, Megaphone, Settings, X, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'matchitup_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'matchitup_cookie_preferences';

/**
 * DPDPA 2023 Compliant Cookie Consent Banner
 * Digital Personal Data Protection Act, 2023 (India)
 */
const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required, cannot be disabled
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay before showing banner for better UX
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPrefs) {
        const parsed = JSON.parse(savedPrefs);
        setPreferences(parsed);
        applyPreferences(parsed);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreferences = (prefs) => {
    // Apply analytics preference
    if (prefs.analytics) {
      enableGoogleAnalytics();
    } else {
      disableGoogleAnalytics();
    }
    
    // Marketing cookies would be handled here when implemented
    // Functional cookies would be handled here when implemented
  };

  const enableGoogleAnalytics = () => {
    // Enable GA tracking
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const disableGoogleAnalytics = () => {
    // Disable GA tracking
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
    // Clear any existing GA cookies
    document.cookie.split(";").forEach((c) => {
      if (c.trim().startsWith("_ga") || c.trim().startsWith("_gid")) {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      }
    });
  };

  const saveConsent = (prefs) => {
    const consentData = {
      timestamp: new Date().toISOString(),
      version: '1.0',
      preferences: prefs
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    applyPreferences(prefs);
    setShowBanner(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const handleRejectNonEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setPreferences(essentialOnly);
    saveConsent(essentialOnly);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const togglePreference = (key) => {
    if (key === 'essential') return; // Cannot disable essential
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const cookieCategories = [
    {
      id: 'essential',
      name: 'Essential Cookies',
      description: 'Required for the platform to function. These enable core features like authentication, security, and session management.',
      icon: Shield,
      required: true,
      examples: ['Authentication tokens', 'Security cookies', 'Session management']
    },
    {
      id: 'analytics',
      name: 'Analytics Cookies',
      description: 'Help us understand how you use Match It Up© so we can improve your experience. Data is anonymized.',
      icon: BarChart3,
      required: false,
      examples: ['Google Analytics', 'Usage patterns', 'Feature popularity']
    },
    {
      id: 'functional',
      name: 'Functional Cookies',
      description: 'Remember your preferences and settings to provide a personalized experience.',
      icon: Settings,
      required: false,
      examples: ['Language preferences', 'Theme settings', 'Display options']
    },
    {
      id: 'marketing',
      name: 'Marketing Cookies',
      description: 'Used to deliver relevant advertisements and measure campaign effectiveness. Currently not in use.',
      icon: Megaphone,
      required: false,
      examples: ['Ad targeting', 'Campaign tracking', 'Retargeting'],
      comingSoon: true
    }
  ];

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed bottom-0 left-0 right-0 z-[9999] p-2 md:p-3"
        data-testid="cookie-consent-banner"
      >
        <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-3 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-amber-500 rounded-lg flex items-center justify-center">
                <Cookie className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">We Value Your Privacy</h3>
                <p className="text-slate-400 text-[10px]">DPDPA 2023 Compliant</p>
              </div>
            </div>
            <button
              onClick={handleRejectNonEssential}
              className="text-slate-400 hover:text-white transition-colors p-1.5"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="p-3">
            <p className="text-slate-600 text-xs mb-2.5">
              Match It Up uses cookies and similar technologies to ensure smooth functioning of the platform, personalise your experience, and analyse usage patterns. You can choose to accept all cookies, reject non-essential ones, or customise your preferences. For more details, please visit our <Link to="/cookie-policy" className="text-[#E8430A] hover:underline font-medium">Cookies Policy</Link>.
            </p>

            {/* Quick Actions */}
            <div className="flex flex-col sm:flex-row gap-2 mb-2.5">
              <button
                onClick={handleAcceptAll}
                className="flex-1 text-white font-medium py-1.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-1.5 text-xs hover:opacity-85"
                style={{ background: '#E8430A' }}
                data-testid="accept-all-cookies"
              >
                <Check className="w-3 h-3" />
                Accept All
              </button>
              <button
                onClick={handleRejectNonEssential}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-1.5 px-4 rounded-lg transition-colors text-xs"
                data-testid="reject-non-essential"
              >
                Essential Only
              </button>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex-1 border border-slate-200 hover:border-slate-300 text-slate-700 font-medium py-1.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-1.5 text-xs"
                data-testid="customize-cookies"
              >
                Customize
                {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            </div>

            {/* Detailed Preferences */}
            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="border-t border-slate-200 pt-3 mt-2">
                    <h4 className="font-semibold text-slate-800 mb-2 text-sm">Cookie Preferences</h4>
                    <div className="space-y-2">
                      {cookieCategories.map((category) => (
                        <div
                          key={category.id}
                          className={`border rounded-lg p-3 transition-colors ${
                            preferences[category.id] 
                              ? 'border-orange-200 bg-orange-50/50' 
                              : 'border-slate-200 bg-slate-50/50'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-2">
                              <div className={`w-7 h-7 rounded-md flex items-center justify-center ${
                                preferences[category.id] ? 'bg-orange-100 text-[#E8430A]' : 'bg-slate-200 text-slate-500'
                              }`}>
                                <category.icon className="w-3.5 h-3.5" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-slate-800 text-sm">{category.name}</span>
                                  {category.required && (
                                    <span className="text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">Required</span>
                                  )}
                                  {category.comingSoon && (
                                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Not in use</span>
                                  )}
                                </div>
                                <p className="text-xs text-slate-500 mt-0.5">{category.description}</p>
                              </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer ml-3">
                              <input
                                type="checkbox"
                                checked={preferences[category.id]}
                                onChange={() => togglePreference(category.id)}
                                disabled={category.required}
                                className="sr-only peer"
                                data-testid={`cookie-toggle-${category.id}`}
                              />
                              <div className={`w-9 h-5 rounded-full peer-focus:ring-2 peer-focus:ring-orange-300 transition-colors ${
                                category.required 
                                  ? 'bg-[#E8430A]/60 cursor-not-allowed' 
                                  : preferences[category.id] 
                                    ? 'bg-[#E8430A]' 
                                    : 'bg-slate-300'
                              }`}>
                                <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                                  preferences[category.id] ? 'translate-x-4' : 'translate-x-0'
                                }`} />
                              </div>
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={handleSavePreferences}
                      className="w-full mt-3 bg-slate-900 hover:bg-slate-800 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
                      data-testid="save-cookie-preferences"
                    >
                      Save My Preferences
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Footer Links */}
            <div className="flex flex-wrap items-center justify-center gap-3 mt-2.5 pt-2.5 border-t border-slate-100 text-[10px]">
              <Link to="/privacy-policy" className="text-[#E8430A] hover:opacity-75 underline">
                Privacy Policy
              </Link>
              <span className="text-slate-300">•</span>
              <Link to="/terms-of-service" className="text-[#E8430A] hover:opacity-75 underline">
                Terms of Service
              </Link>
              <span className="text-slate-300">•</span>
              <span className="text-slate-500">
                Data Fiduciary: Match It Up©
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Hook to check if a specific cookie category is enabled
 */
export const useCookieConsent = () => {
  const [consent, setConsent] = useState({
    essential: true,
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (savedPrefs) {
      setConsent(JSON.parse(savedPrefs));
    }
  }, []);

  const isConsentGiven = (category) => consent[category] || false;
  
  const resetConsent = () => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    localStorage.removeItem(COOKIE_PREFERENCES_KEY);
    window.location.reload();
  };

  return { consent, isConsentGiven, resetConsent };
};

export default CookieConsent;
